import React, { useCallback, memo, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { ImageLibraryItem } from '@/hooks/runware/image-library/types';
import { motion } from 'framer-motion';
import { useImageLibrary } from '@/hooks/runware/image-library/use-image-library';
import PersistentFavoriteButton from '@/components/ui/PersistentFavoriteButton';
import AddToLibraryButton from '@/components/universal-storage/AddToLibraryButton';
import { PublicImageAddToProjectButton } from '@/components/image-generator';
import AddToProjectButton from '@/components/private-image-generator/AddToProjectButton';
import { useImageCard } from '@/hooks/image-generator/use-image-card';

interface ImageCardProps {
  image: ImageLibraryItem;
  onDelete: (id: string) => void;
  onSelect: (image: ImageLibraryItem) => void;
  index?: number;
  onImageLoaded?: (id: string) => void;
  contentType?: 'image' | 'research' | 'workbook' | 'app_idea' | 'private_image'; // NEW: Optional contentType prop
  hideStorageButton?: boolean; // NEW: Optional prop to hide storage button
}

const ImageCard: React.FC<ImageCardProps> = memo(({ 
  image, 
  onDelete,
  onSelect,

  onImageLoaded,
  contentType = "image", // NEW: Default to "image" for backward compatibility
  hideStorageButton = false // NEW: Default to false for backward compatibility
}) => {
  const { toggleFavorite } = useImageLibrary();
  
  // Use the legacy hook that matches the 4-parameter call pattern
  const imageCardHandler = useImageCard(
    image,
    onDelete,
    onSelect,
    onImageLoaded
  );
  
  // Reference to the img element for caching after load
  const imgRef = useRef<HTMLImageElement>(null);
  
  // Use original URL directly - we'll cache after successful load
  const imageUrl = image?.image_url || '';
  
  // Enhanced image load handler with caching
  const handleImageLoad = useCallback(() => {
    // Call the original handler
    imageCardHandler.handleImageLoad();
    
    // Cache the loaded image using the img element
    if (imgRef.current && image?.image_url) {
      imageCardHandler.cacheLoadedImage(image.image_url, imgRef.current);
    }
  }, [image?.image_url, imageCardHandler]);
  
  // Handle favorite toggle with Promise<boolean> return type
  const handleFavoriteToggle = useCallback(async (itemId: string): Promise<void> => {
    try {
      await toggleFavorite.mutateAsync(itemId);
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
    }
  }, [toggleFavorite]);
  
  // Memoize formatted date to prevent recalculation on every render
  // Date formatting removed as it was unused
  /*const formattedDate = useMemo(() => 
    formatDistanceToNow(new Date(image.created_at), { addSuffix: true })
  , [image.created_at]);*/
  
  return (
    <motion.div
      initial={{ 
        opacity: 0, 
        scale: 0.9,
        y: 10
      }}
      animate={{ 
        opacity: 1, 
        scale: 1,
        y: 0
      }}
      exit={{ 
        opacity: 0, 
        scale: 0.9,
        y: -10
      }}
      whileHover={{ 
        scale: 1.02,
        y: -2
      }}
      whileTap={{ scale: 0.98 }}
      transition={{
        duration: 0.2,
        ease: [0.25, 0.1, 0.25, 1.0]
      }}
      className="cursor-pointer"
    >
      <motion.div 
        className="overflow-hidden h-full shadow-md rounded-lg border bg-card text-card-foreground"
        whileHover={{
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
        }}
        transition={{
          duration: 0.2,
          ease: [0.25, 0.1, 0.25, 1.0]
        }}
      >
        <motion.div 
          className="relative aspect-[1] overflow-hidden h-full"
          onClick={imageCardHandler.handleSelect}
          data-test-id="image-card"
          // Disable layout animations completely
          layout={false}
        >
          {/* Image with layoutId for smooth transitions to modal */}
          <motion.img
            ref={imgRef}
            layoutId={`image-card-${image?.id}`}
            src={imageUrl}
            alt={image?.description || "Generated image"}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover"
            data-test-id="image-card-img"
            transition={{ 
              type: "spring",
              stiffness: 200,
              damping: 25,
              mass: 0.8
            }}
            // Add hardware acceleration
            style={{ willChange: 'transform' }}
            // Notify when image has loaded and cache the image
            onLoad={handleImageLoad}
          />
          
          {/* Favorite button - top right - only show for non-private images */}
          {contentType !== 'private_image' && (
            <motion.div 
              className="absolute top-2 right-2 z-10"
              initial={{ scale: 0, opacity: 0 }}
              animate={{ 
                scale: 1, 
                opacity: 1,
                transition: {
                  type: "spring",
                  stiffness: 180,
                  damping: 28,
                  delay: 0.05
                }
              }}
              style={{ willChange: 'transform, opacity' }}
            >
              <PersistentFavoriteButton
                itemId={image?.id || ''}
                onToggleFavorite={handleFavoriteToggle}
                isFavorite_prop={image?.is_favorite || false}
                favoriteCount_prop={image?.favorite_count || 0}
                forceWhiteText={true}
                className="transform scale-90 origin-top-right" // Scale down slightly and position from top-right
              />
            </motion.div>
          )}

          {/* Bottom right: Storage and Project buttons side by side */}
          <div className="absolute bottom-2 right-2 z-10 flex flex-row gap-2">
            {/* Project button - for both public and private images */}
            {contentType !== 'private_image' ? (
              <motion.div 
                initial={{ scale: 0, opacity: 0 }}
                animate={{ 
                  scale: 1, 
                  opacity: 1,
                  transition: {
                    type: "spring",
                    stiffness: 180,
                    damping: 28,
                    delay: 0.05
                  }
                }}
                style={{ willChange: 'transform, opacity' }}
              >
                <PublicImageAddToProjectButton
                  itemId={image?.id || ''}
                  dropdownLabel="Add to Project"
                  variant="auto"
                  forceWhiteText={true}
                />
              </motion.div>
            ) : (
              <motion.div 
                initial={{ scale: 0, opacity: 0 }}
                animate={{ 
                  scale: 1, 
                  opacity: 1,
                  transition: {
                    type: "spring",
                    stiffness: 180,
                    damping: 28,
                    delay: 0.05
                  }
                }}
                style={{ willChange: 'transform, opacity' }}
              >
                <AddToProjectButton
                  itemId={image?.id || ''}
                  dropdownLabel="Add to Project"
                  variant="auto"
                  forceWhiteText={true}
                  contentType="private_image"
                />
              </motion.div>
            )}

            {/* Storage button */}
            {!hideStorageButton && (
              <motion.div 
                initial={{ scale: 0, opacity: 0 }}
                animate={{ 
                  scale: 1, 
                  opacity: 1,
                  transition: {
                    type: "spring",
                    stiffness: 180,
                    damping: 28,
                    delay: 0.1
                  }
                }}
                style={{ willChange: 'transform, opacity' }}
              >
                <AddToLibraryButton
                  itemId={image?.id || ''}
                  contentType={contentType}
                  dropdownLabel="Add to Storage"
                  variant="auto"
                  forceWhiteText={true}
                />
              </motion.div>
            )}
          </div>
          {/* No closing AnimatePresence tag needed */}
        </motion.div>
      </motion.div>
    </motion.div>
  );
}, (prevProps, nextProps) => {
  // More comprehensive comparison to prevent unnecessary re-renders
  
  // First check if the handlers are the same references
  if (prevProps.onDelete !== nextProps.onDelete ||
      prevProps.onSelect !== nextProps.onSelect ||
      prevProps.onImageLoaded !== nextProps.onImageLoaded) {
    return false;
  }
  
  // Then check critical image properties that would affect rendering
  return (
    prevProps.image?.id === nextProps.image?.id &&
    prevProps.image?.is_favorite === nextProps.image?.is_favorite &&
    prevProps.image?.image_url === nextProps.image?.image_url &&
    prevProps.image?.created_at === nextProps.image?.created_at
  );
});

export default ImageCard;
